import { useCallback, useEffect, useRef, type KeyboardEvent } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { useAutoResizeTextarea } from "@/components/ui/use-auto-resize-textarea";
import { useT } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import type { useCommentAction } from "@/hooks/use-comment-action";
import { useAuth } from "@/hooks/use-auth";
import type { AgentAvatarStyleKey } from "@/lib/dicebear-styles";
import { useCommentDictationClaim } from "@/desktop/use-comment-dictation-claim";
import { useDictation } from "@/desktop/use-dictation";
import {
  useStandaloneInputDrag,
  type DragBarParts,
} from "@/desktop/use-standalone-input-drag";
import { CommentEditorField } from "@/components/comments/comment-editor-field";
import {
  InputBarMicButton,
  InputBarSendButton,
} from "@/components/panels/input-bar-actions";
import { INPUT_BAR_FIELD_CLASS } from "@/components/panels/input-bar-shell";

type CommentAction = ReturnType<typeof useCommentAction>;

/** Width of the composer; the canvas toolbar keeps it inside the viewport. */
export const SELECTION_COMPOSER_WIDTH = 400;
/** Four 20px lines plus the field's 8px vertical padding, then it scrolls. */
const FIELD_MAX_HEIGHT = 88;
/** Dragged like the standalone input bar: anywhere but its controls. */
const COMPOSER_PARTS: DragBarParts = {
  shell: '[data-testid="selection-ask-form"]',
  field: '[data-testid="selection-ask-input"]',
};

export interface SelectionComposerProps {
  /** The `useCommentAction` state: text, agent and frame mentions. */
  comment: CommentAction;
  selectedText: string;
  /** Owner id of the composer's dictation claim. */
  dictationId: string;
  /** Whether a non-empty text can be sent now. */
  canSubmit: boolean;
  onSubmit: () => Promise<void>;
  /** Escape: back to the toolbar buttons. */
  onCancel: () => void;
}

/**
 * The "Ask Denker" composer a selection toolbar swaps itself for, built from
 * the input bar's own parts (mic, field and send chip) on the menu surface.
 * One row: mic, a muted excerpt of the selection, then the field, which grows downward to four lines (agent and frame mentions),
 * and send. Enter or ⌘/Ctrl+Enter sends and Shift+Enter adds a line, as in
 * the input bar; Escape (or clicking away) closes it.
 */
export function SelectionComposer({
  comment,
  selectedText,
  dictationId,
  canSubmit,
  onSubmit,
  onCancel,
}: SelectionComposerProps) {
  const t = useT(canvasMessages);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dictationStatus = useDictation((s) => s.status);
  const { user } = useAuth();
  const avatarStyleKey = (user?.agent_avatar_style ??
    "glass") as AgentAvatarStyleKey;
  const commentTextRef = useRef(comment.text);
  commentTextRef.current = comment.text;
  useAutoResizeTextarea(inputRef, comment.text, FIELD_MAX_HEIGHT);
  // Drag it off the text it covers, as the standalone input bar moves.
  const drag = useStandaloneInputDrag(null, true, undefined, COMPOSER_PARTS);

  const dictation = useCommentDictationClaim({
    id: dictationId,
    disableScreenshotCapture: true,
    deliverTranscript: (text) => {
      const current = commentTextRef.current.trimEnd();
      comment.handleTextChange(current ? `${current}\n${text}` : text);
      requestAnimationFrame(() => inputRef.current?.focus());
    },
  });
  const toggleDictation = dictation.toggle;

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, []);

  const ready = Boolean(comment.text.trim()) && canSubmit;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter that commits an IME candidate (WebKit reports keyCode 229).
      if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
      if (comment.handleMentionKeyDown(e.key)) {
        e.preventDefault();
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (ready) void onSubmit();
        return;
      }
      const mentionMenuOpen =
        comment.showMentions && comment.mentionOptions.length > 0;
      if (e.key === "Escape" && !mentionMenuOpen) {
        // Escape closes this composer only, not the toolbar hosting it.
        e.stopPropagation();
        onCancel();
      }
    },
    [comment, onCancel, onSubmit, ready],
  );

  return (
    <div ref={drag.containerRef} className="w-fit" {...drag.dragHandlers}>
      <div
        className={cn(
          // The menu surface of the pill it replaces: the input bar's plate
          // sits under the opened item and would not show.
          surfaceRoleClassName("menu"),
          "flex min-h-[40px] max-w-[calc(100vw-16px)] items-end gap-1 p-1.5",
          "[--radius-surface-menu:var(--radius-surface-rail)] [--denk-surface-radius-menu:var(--radius-surface-rail)]",
        )}
        style={{ width: SELECTION_COMPOSER_WIDTH }}
        {...surfaceRoleAttributes("menu", {
          nativeLevel: "root",
          nativeGroup: "menus",
        })}
        data-testid="selection-ask-form"
      >
        <InputBarMicButton
          mic={{
            recording: dictationStatus === "recording",
            disabled: dictationStatus === "processing",
            onClick: () => void toggleDictation(),
          }}
          data-testid="selection-ask-voice"
        />
        <span
          className="flex h-7 max-w-[30%] shrink-0 items-center gap-1 self-start text-appkit-caption text-muted"
          title={selectedText}
          data-testid="selection-ask-quote"
        >
          <Icons.TextQuote className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">
            {selectedText.replace(/\s+/g, " ").trim()}
          </span>
        </span>
        <CommentEditorField
          appearance="plain"
          avatarStyleKey={avatarStyleKey}
          selectedAgent={comment.selectedAgent}
          onClearAgent={comment.clearAgent}
          text={comment.text}
          onTextChange={comment.handleTextChange}
          onKeyDown={handleKeyDown}
          textareaRef={inputRef}
          showMentions={comment.showMentions}
          mentionOptions={comment.mentionOptions}
          mentionIndex={comment.mentionIndex}
          onMentionSelect={comment.selectMentionOption}
          rows={1}
          inputTestId="selection-ask-input"
          removeAgentTestId="selection-agent-remove"
          mentionMenuTestId="selection-ask-mentions"
          placeholder={t("askDenkerPlaceholder")}
          className="min-w-0"
          textareaClassName={cn(INPUT_BAR_FIELD_CLASS, "flex-none")}
        />
        <InputBarSendButton
          type="button"
          active={ready}
          disabled={!ready}
          onClick={() => void onSubmit()}
          aria-label={t("askDenkerSend")}
          title={t("askDenkerSend")}
          data-testid="selection-ask-send"
        >
          <Icons.ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
        </InputBarSendButton>
      </div>
    </div>
  );
}
